/**
 * Shop Show More Web Component
 * A lightweight Light DOM component that wraps server-rendered content
 * and adds a toggle button to show/hide items.
 *
 * Attributes:
 * - label: Collapsed button text (default "See more")
 * - label-expanded: Expanded state text (default "See less")
 * - target-class: CSS class toggled on the HOST element (default "show-more--expanded")
 * - expanded: If present, starts in expanded state (e.g. for paginated pages > 1)
 *
 * Example:
 *   <shop-show-more label="See more" label-expanded="See less">
 *     <ul>
 *       <li>Item 1</li>
 *       <li class="hidden">Item 2</li>
 *     </ul>
 *   </shop-show-more>
 */

const ShopShowMore = (function () {
  const TAG_NAME = "shop-show-more";

  class ShopShowMore extends HTMLElement {
    static get observedAttributes() {
      return ["label", "label-expanded", "expanded"];
    }

    attributeChangedCallback(name, oldValue, newValue) {
      if (!this._toggle) return;

      if (name === "label") {
        this._label = newValue || "See more";
        if (!this._expanded) {
          this._toggle.setAttribute("label", this._label);
        }
      } else if (name === "label-expanded") {
        this._labelExpanded = newValue || "See less";
        if (this._expanded) {
          this._toggle.setAttribute("label", this._labelExpanded);
        }
      } else if (name === "expanded") {
        this._expanded = newValue === "true";
        this.classList.toggle(this._targetClass, this._expanded);
        this._toggle.setAttribute("aria-expanded", String(this._expanded));
        this._toggle.setAttribute(
          "label",
          this._expanded ? this._labelExpanded : this._label,
        );
      }
    }

    connectedCallback() {
      if (this._initialized) return;
      this._initialized = true;

      // Defer to next macrotask so ALL children are parsed before we appendChild.
      // A microtask (Promise.resolve) can fire at a parser checkpoint before
      // children exist, causing the button to land at the top of the element.
      setTimeout(() => this._render(), 0);
    }

    // Attribute values here are set via setAttribute, which never parses HTML —
    // no escaping needed, and escapeHTML would leave literal entities (e.g.
    // "&#39;") in the rendered label instead of decoding them.
    _render() {
      this._label = this.getAttribute("label") || "See more";
      this._labelExpanded = this.getAttribute("label-expanded") || "See less";
      this._targetClass =
        this.getAttribute("target-class") || "show-more--expanded";
      this._expanded = this.getAttribute("expanded") === "true";

      // Create toggle button
      this._toggle = document.createElement("fragno-button");
      this._toggle.classList.add("show-more__toggle");
      this._toggle.setAttribute("hierarchy", "secondary");
      this._toggle.setAttribute("size", "md");
      this._toggle.setAttribute(
        "label",
        this._expanded ? this._labelExpanded : this._label,
      );
      this._toggle.setAttribute("aria-expanded", String(this._expanded));
      this.appendChild(this._toggle);

      // If starting expanded, apply the target class
      if (this._expanded) {
        this.classList.add(this._targetClass);
      }

      this._handleClick = () => {
        this._expanded = !this._expanded;
        this.classList.toggle(this._targetClass, this._expanded);
        this._toggle.setAttribute(
          "label",
          this._expanded ? this._labelExpanded : this._label,
        );
        this._toggle.setAttribute("aria-expanded", String(this._expanded));
      };

      this._toggle.addEventListener("click", this._handleClick);
    }

    disconnectedCallback() {
      if (this._toggle && this._handleClick) {
        this._toggle.removeEventListener("click", this._handleClick);
      }
    }
  }

  if (!customElements.get(TAG_NAME)) {
    customElements.define(TAG_NAME, ShopShowMore);
  }

  return ShopShowMore;
})();

export { ShopShowMore };
